import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { api } from "@/lib/api";
import type { GenerateRequest, GenerateResponse, GenerationStatusResponse } from "@/types";

const SESSION_KEY = "cartoontale_active_generations";

export function registerGenerationId(id: string) {
  try {
    const ids: string[] = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]");
    if (!ids.includes(id)) ids.push(id);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(ids));
  } catch {}
}

export function isGenerationAllowed(id: string): boolean {
  try {
    const ids: string[] = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]");
    return ids.includes(id);
  } catch {
    return false;
  }
}

export function useGenerate() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: GenerateRequest) => {
      if (!user?.id) throw new Error("No user ID");
      return api.post<GenerateResponse>("/generate", params);
    },
    onSuccess: (data) => {
      if (data?.video?.id) registerGenerationId(data.video.id);
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

export function useGenerationStatus(generationId: string | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["generation", generationId],
    queryFn: async () => {
      if (!generationId) throw new Error("No generation ID");
      return api.get<GenerationStatusResponse>(
        `/generation/${generationId}/status`,
      );
    },
    enabled: !!generationId,
    refetchInterval: (query) => {
      const status = query.state.data?.generation?.status;
      if (status === "completed" || status === "failed") {
        queryClient.invalidateQueries({ queryKey: ["videos"] });
        queryClient.invalidateQueries({ queryKey: ["home-recent-videos"] });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        return false;
      }
      return 3000;
    },
    refetchIntervalInBackground: true,
    retry: false,
  });
}
